import { IconButton } from './IconButton.js';

/**
 * Owns every icon control button in the left/right sidebars — Grids, Reset,
 * Auto-start, Help, Settings, Leaderboard, the Vortex trio (Hub / Activate /
 * Delete), the restore + scoring buttons, the export/import dev tools, and the
 * admin toggles — plus the shared label+glyph swap helper.
 *
 * It resolves the refs from the DOM and centralizes {@link setIconButton} (the
 * back-arrow ↔ default-glyph swap) so that logic lives in one place. App still
 * owns the *policy* (when to show/label each button); SidebarController is just
 * the ref holder + DOM mechanism. Field names mirror the element ids and match
 * the names App used before extraction, so call sites read `this.sidebar.gridsBtn`
 * with no behavioural change.
 */
export class SidebarController {
    // Buttons that double as in-modal "Back to …" controls — label+glyph swap.
    readonly grids: IconButton;
    readonly help: IconButton;
    readonly settings: IconButton;
    readonly leaderboard: IconButton;

    // Plain control buttons (some set their own label text directly, no glyph swap).
    readonly resetBtn: HTMLButtonElement;
    readonly resetLabelEl: HTMLElement;
    readonly autoStartBtn: HTMLButtonElement;
    readonly exportStateBtn: HTMLButtonElement;
    readonly exportDataBtn: HTMLButtonElement;
    readonly importDataBtn: HTMLButtonElement;
    readonly restoreBestBtn: HTMLButtonElement;
    readonly restoreBackupBtn: HTMLButtonElement;
    readonly exitTutorialBtn: HTMLButtonElement;
    readonly vortexHubBtn: HTMLButtonElement;
    readonly vortexHubLabelEl: HTMLElement;
    readonly vortexActivateBtn: HTMLButtonElement;
    readonly vortexDeleteBtn: HTMLButtonElement;
    readonly vortexDeleteLabelEl: HTMLElement;
    readonly scoringRulesBtn: HTMLButtonElement;
    readonly welcomeCloseBtn: HTMLButtonElement;
    readonly adminBtn: HTMLButtonElement;
    readonly adminBackBtn: HTMLButtonElement;

    constructor() {
        const btn = (id: string) => document.getElementById(id) as HTMLButtonElement;
        const labelOf = (b: HTMLElement) => b.querySelector('.icon-btn-label') as HTMLElement;

        this.grids = new IconButton('grids-btn');
        this.help = new IconButton('help-btn');
        this.settings = new IconButton('settings-btn');
        this.leaderboard = new IconButton('leaderboard-btn');

        this.resetBtn = btn('reset');
        this.resetLabelEl = labelOf(this.resetBtn);
        this.autoStartBtn = btn('auto-start');
        this.exportStateBtn = btn('export-state');
        this.exportDataBtn = btn('export-data');
        this.importDataBtn = btn('import-data');
        this.restoreBestBtn = btn('restore-best');
        this.restoreBackupBtn = btn('restore-backup');
        this.exitTutorialBtn = btn('exit-tutorial');
        this.vortexHubBtn = btn('vortex-hub-btn');
        this.vortexHubLabelEl = labelOf(this.vortexHubBtn);
        this.vortexActivateBtn = btn('vortex-activate-btn');
        this.vortexDeleteBtn = btn('vortex-delete-btn');
        this.vortexDeleteLabelEl = labelOf(this.vortexDeleteBtn);
        this.scoringRulesBtn = btn('scoring-rules-btn');
        this.welcomeCloseBtn = btn('welcome-close-btn');
        this.adminBtn = btn('admin-btn');
        this.adminBackBtn = btn('admin-back-btn');
    }
}
